import React, { useRef } from 'react';
import type { ModuleId, ReportData } from '../data/types';
import { MODULES, MODULE_META_EXTRA } from '../modules/moduleRegistry';
import { ScreenCover, ScreenPage } from '../primitives/ScreenPrimitives';
import { ScreenToc, ScreenDisclaimer, type TocEntry } from '../primitives/staticPages';
import type { SubOptionsByModule } from '../subOptionState';
import { RenderTargetProvider } from '../primitives/RenderTarget';
import { ScreenDirectorDetail } from '../modules/Management';
import { fmtDate } from '../modules/formatUtils';
import { reportViewFor } from '../data/normalizers';
import {
    DASH,
    REPORT_ANCHOR_ATTR,
    reportAnchorId,
    SUB_OPTION_CONFIRMED_ID,
    SUB_OPTION_DIRECTOR_DETAILS_ID,
    SUB_OPTION_PROBABLE_ID,
    subOptionOn,
    REPORT_MODULE_ID,
} from '../constants';

export type ReportPreviewProps = {
    data: ReportData;
    enabledModules: ModuleId[];
    subOptions?: SubOptionsByModule;
    generatedOn?: string;
    pnoLogoUrl?: string;
};

export const ReportPreview: React.FC<ReportPreviewProps> = ({ data, enabledModules, subOptions, generatedOn, pnoLogoUrl }) => {
    const today = generatedOn ?? fmtDate(new Date().toISOString());
    const meta: Array<{ label: string; value: string }> = [];
    if (data.identity.cin && data.identity.cin !== DASH) meta.push({ label: 'CIN', value: data.identity.cin });
    if (data.identity.pan && data.identity.pan !== DASH) meta.push({ label: 'PAN', value: data.identity.pan });
    if (data.identity.sector && data.identity.sector !== DASH) meta.push({ label: 'Sector', value: data.identity.sector });
    if (data.identity.state) meta.push({ label: 'HQ', value: data.identity.state });
    if (data.identity.foundationDate) meta.push({ label: 'Founded', value: data.identity.foundationDate });
    if (data.score?.companyScore && Number.isFinite(data.score.companyScore) && data.score.companyScore > 0) {
        const scaled = (data.score.companyScore / 4) * 10;
        meta.push({ label: 'AICA Risk Score', value: `${scaled.toFixed(1)} / 10` });
    }

    const renderable = enabledModules.map((id) => MODULES[id as keyof typeof MODULES]).filter((m) => m && m.hasData(data));
    const legalSwitches = enabledModules.includes('legalRisk') ? subOptions?.legalRisk : undefined;
    const view = reportViewFor(data, {
        probableOn: subOptionOn(legalSwitches, SUB_OPTION_PROBABLE_ID),
        confirmedOn: subOptionOn(legalSwitches, SUB_OPTION_CONFIRMED_ID),
    });

    const tocEntries: TocEntry[] = renderable.map((m, i) => ({
        index: String(i + 1).padStart(2, '0'),
        title: m.meta.title,
        subtitle: m.meta.description,
        coverage: MODULE_META_EXTRA[m.meta.id]?.coverage,
    }));

    return (
        <div>
            <ScreenCover
                orgName={data.identity.orgName}
                subtitle={[data.identity.sector, data.identity.state].filter(Boolean).join(' · ')}
                meta={meta}
                generatedOn={today}
                pnoLogoUrl={pnoLogoUrl}
            />
            {tocEntries.length > 0 ? <ScreenToc orgName={data.identity.orgName} entries={tocEntries} /> : null}
            {renderable.map((m, i) => {
                const index = String(i + 1).padStart(2, '0');
                // Director detail pages follow the management summary only when the switch is on.
                const showDirectorPages = m.meta.id === 'management' && subOptionOn(subOptions?.management, SUB_OPTION_DIRECTOR_DETAILS_ID);
                const directors = showDirectorPages && data.management ? data.management.profiles : [];
                return (
                    <div key={m.meta.id} data-report-module={m.meta.id}>
                        <SectionWrapper
                            orgName={data.identity.orgName}
                            sectionLabel={`${index} · ${m.meta.title}`}
                            pageNumber={i + 1}
                            totalPages={renderable.length}
                        >
                            <RenderTargetProvider target="screen">
                                <m.Screen data={view} index={index} options={subOptions?.[m.meta.id]} />
                            </RenderTargetProvider>
                        </SectionWrapper>
                        {directors.map((profile, di) => (
                            <SectionWrapper
                                key={`director-${di}`}
                                orgName={data.identity.orgName}
                                sectionLabel={`${index} · Director Detail`}
                                pageNumber={i + 1}
                                totalPages={renderable.length}
                                anchorId={
                                    di === 0 ? reportAnchorId(REPORT_MODULE_ID.management, SUB_OPTION_DIRECTOR_DETAILS_ID) : undefined
                                }
                            >
                                <RenderTargetProvider target="screen">
                                    <ScreenDirectorDetail
                                        profile={profile}
                                        moduleIndex={index}
                                        directorIndex={di + 1}
                                        directorTotal={directors.length}
                                        data={view}
                                    />
                                </RenderTargetProvider>
                            </SectionWrapper>
                        ))}
                    </div>
                );
            })}
            <ScreenPage
                orgName={data.identity.orgName}
                sectionLabel="Disclaimer"
                pageNumber={renderable.length + 1}
                totalPages={renderable.length + 1}
            >
                <ScreenDisclaimer />
            </ScreenPage>
        </div>
    );
};

const SectionWrapper = React.forwardRef<
    HTMLDivElement,
    {
        children: React.ReactNode;
        orgName: string;
        sectionLabel: string;
        pageNumber: number;
        totalPages: number;
        anchorId?: string;
    }
>(({ children, orgName, sectionLabel, pageNumber, totalPages, anchorId }, ref) => {
    const innerRef = useRef<HTMLDivElement | null>(null);
    return (
        <div
            {...(anchorId ? { [REPORT_ANCHOR_ATTR]: anchorId } : {})}
            ref={(node) => {
                innerRef.current = node;
                if (typeof ref === 'function') ref(node);
                else if (ref) (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }}
        >
            <ScreenPage orgName={orgName} sectionLabel={sectionLabel} pageNumber={pageNumber} totalPages={totalPages}>
                {children}
            </ScreenPage>
        </div>
    );
});
SectionWrapper.displayName = 'SectionWrapper';
